
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

// =====================================================
// BASE URL
// =====================================================

const baseUrl =
  process.env.REACT_APP_BACKEND_BASE_URL || "http://localhost:5000";

// =====================================================
// TOKEN HELPER
// =====================================================

const getToken = (getState) => {
  return getState()?.auth?.token;
};

// =====================================================
// AXIOS CONFIG
// =====================================================

const getAuthConfig = (token) => ({
  headers: {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  },
});

// =====================================================
// ERROR HELPER
// =====================================================

const getErrorMessage = (err, fallback = "Something went wrong.") => {
  return (
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    fallback
  );
};

// =====================================================
// 🔍 CHECK AVAILABILITY
// =====================================================

export const checkOtherMedicalAvailability = createAsyncThunk(
  "otherMedicalAvailability/check",

  async (_, { getState, rejectWithValue }) => {
    try {
      const token = getToken(getState);

      if (!token) {
        return rejectWithValue("Authentication token missing.");
      }

      const response = await axios.post(
        `${baseUrl}/other-medical/check`,
        {},
        getAuthConfig(token),
      );

      return response.data;
    } catch (err) {
      console.error(
        "❌ Check other medical availability error:",
        err,
      );

      return rejectWithValue(
        getErrorMessage(
          err,
          "Failed to check medical availability.",
        ),
      );
    }
  },
);

// =====================================================
// 📥 GET MY AVAILABILITY
// =====================================================

export const getOtherMedicalAvailability = createAsyncThunk(
  "otherMedicalAvailability/get",

  async (_, { getState, rejectWithValue }) => {
    try {
      const token = getToken(getState);

      if (!token) {
        return rejectWithValue("Authentication token missing.");
      }

      const response = await axios.get(
        `${baseUrl}/other-medical/me`,
        getAuthConfig(token),
      );

      console.log(
        "✅ GET OTHER MEDICAL AVAILABILITY:",
        response.data,
      );

      return response.data;
    } catch (err) {
      console.error(
        "❌ Get other medical availability error:",
        err,
      );

      return rejectWithValue(
        getErrorMessage(
          err,
          "Failed to load medical availability.",
        ),
      );
    }
  },
);

// =====================================================
// 💾 SAVE FULL WEEK
// =====================================================

export const saveOtherMedicalAvailability = createAsyncThunk(
  "otherMedicalAvailability/save",

  async (payload, { getState, rejectWithValue }) => {
    try {
      const token = getToken(getState);

      if (!token) {
        return rejectWithValue("Authentication token missing.");
      }

      const response = await axios.post(
        `${baseUrl}/other-medical/`,
        payload,
        getAuthConfig(token),
      );

      console.log(
        "✅ SAVE FULL WEEK:",
        response.data,
      );

      return response.data;
    } catch (err) {
      console.error(
        "❌ Save full week error:",
        err,
      );

      return rejectWithValue(
        getErrorMessage(
          err,
          "Failed to save week availability.",
        ),
      );
    }
  },
);

// =====================================================
// 🧩 UPDATE SINGLE DAY
// =====================================================

export const updateSingleDay = createAsyncThunk(
  "otherMedicalAvailability/updateDay",

  async (data, { getState, rejectWithValue }) => {
    try {
      const token = getToken(getState);

      if (!token) {
        return rejectWithValue("Authentication token missing.");
      }

      const payload = {
        ...data,
      };

      // Prevent duplicate token field inside body
      delete payload.token;

      const response = await axios.put(
        `${baseUrl}/other-medical/day`,
        payload,
        getAuthConfig(token),
      );

      console.log(
        "✅ UPDATE SINGLE DAY:",
        response.data,
      );

      return response.data;
    } catch (err) {
      console.error(
        "❌ Update single day error:",
        err,
      );

      return rejectWithValue(
        getErrorMessage(
          err,
          "Failed to update day availability.",
        ),
      );
    }
  },
);

// =====================================================
// ⏱️ UPDATE SINGLE SLOT
// =====================================================

export const updateSingleSlot = createAsyncThunk(
  "otherMedicalAvailability/updateSlot",

  async (data, { getState, rejectWithValue }) => {
    try {
      const token = getToken(getState);

      if (!token) {
        return rejectWithValue("Authentication token missing.");
      }

      const payload = {
        ...data,
      };

      // Token should be sent through Authorization header
      // rather than request body.
      delete payload.token;

      console.log(
        "💾 UPDATE SINGLE SLOT PAYLOAD:",
        payload,
      );

      const response = await axios.put(
        `${baseUrl}/other-medical/slot`,
        payload,
        getAuthConfig(token),
      );

      console.log(
        "✅ UPDATE SINGLE SLOT RESPONSE:",
        response.data,
      );

      return response.data;
    } catch (err) {
      console.error(
        "❌ Update single slot error:",
        err,
      );

      return rejectWithValue(
        getErrorMessage(
          err,
          "Failed to save slot.",
        ),
      );
    }
  },
);

// =====================================================
// 🧹 CLEAR WEEK
// =====================================================
//
// Backend route:
//
// router.delete(
//   "/clear-week",
//   authMiddleware,
//   clearWeek
// );
//
// Therefore frontend:
//
// DELETE /api/other-medical/clear-week
//
// =====================================================

export const clearWeek = createAsyncThunk(
  "otherMedicalAvailability/clearWeek",

  async (_, { getState, rejectWithValue }) => {
    try {
      const token = getToken(getState);

      if (!token) {
        return rejectWithValue("Authentication token missing.");
      }

      const url = `${baseUrl}/other-medical/clear-week`;

      console.log("🧹 CLEAR WEEK REQUEST:", {
        method: "DELETE",
        url,
      });

      const response = await axios.delete(
        url,
        getAuthConfig(token),
      );

      console.log(
        "✅ CLEAR WEEK RESPONSE:",
        response.data,
      );

      return response.data;
    } catch (err) {
      console.error(
        "❌ CLEAR WEEK API ERROR:",
        err,
      );

      console.error(
        "❌ CLEAR WEEK STATUS:",
        err?.response?.status,
      );

      console.error(
        "❌ CLEAR WEEK RESPONSE:",
        err?.response?.data,
      );

      if (err?.response?.status === 404) {
        return rejectWithValue(
          "Clear week API route not found. Please restart the backend and verify that /api/other-medical is mounted.",
        );
      }

      return rejectWithValue(
        getErrorMessage(
          err,
          "Failed to clear week availability.",
        ),
      );
    }
  },
);

// =====================================================
// ❌ DELETE FULL AVAILABILITY
// =====================================================

export const deleteAvailability = createAsyncThunk(
  "otherMedicalAvailability/delete",

  async (_, { getState, rejectWithValue }) => {
    try {
      const token = getToken(getState);

      if (!token) {
        return rejectWithValue("Authentication token missing.");
      }

      const url = `${baseUrl}/other-medical/`;

      console.log(
        "🗑️ DELETE ALL AVAILABILITY:",
        url,
      );

      const response = await axios.delete(
        url,
        getAuthConfig(token),
      );

      console.log(
        "✅ DELETE ALL RESPONSE:",
        response.data,
      );

      return response.data;
    } catch (err) {
      console.error(
        "❌ Delete all availability error:",
        err,
      );

      return rejectWithValue(
        getErrorMessage(
          err,
          "Failed to delete all availability.",
        ),
      );
    }
  },
);

// =====================================================
// 📦 INITIAL STATE
// =====================================================

const initialState = {
  availability: null,

  exists: false,

  loading: false,

  saving: false,

  actionLoading: false,

  error: null,

  successMessage: null,
};

// =====================================================
// 🧠 SLICE
// =====================================================

const otherMedicalAvailabilitySlice = createSlice({
  name: "otherMedicalAvailability",

  initialState,

  reducers: {
    resetState: (state) => {
      state.availability = null;

      state.exists = false;

      state.loading = false;

      state.saving = false;

      state.actionLoading = false;

      state.error = null;

      state.successMessage = null;
    },

    clearError: (state) => {
      state.error = null;
    },

    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
  },

  extraReducers: (builder) => {
    builder

      // ===================================================
      // CHECK
      // ===================================================

      .addCase(
        checkOtherMedicalAvailability.pending,
        (state) => {
          state.error = null;
        },
      )

      .addCase(
        checkOtherMedicalAvailability.fulfilled,
        (state, action) => {
          state.error = null;

          /*
           * Supports:
           * { exists: true }
           * { data: ... }
           * true / false
           */
          if (
            typeof action.payload === "boolean"
          ) {
            state.exists = action.payload;
          } else {
            state.exists = Boolean(
              action.payload?.exists ??
                action.payload?.data ??
                action.payload,
            );
          }
        },
      )

      .addCase(
        checkOtherMedicalAvailability.rejected,
        (state, action) => {
          state.error =
            action.payload ||
            "Failed to check availability.";
        },
      )

      // ===================================================
      // GET
      // ===================================================

      .addCase(
        getOtherMedicalAvailability.pending,
        (state) => {
          state.loading = true;

          state.error = null;
        },
      )

      .addCase(
        getOtherMedicalAvailability.fulfilled,
        (state, action) => {
          state.loading = false;

          state.error = null;

          state.availability =
            action.payload?.data ?? [];

          state.exists =
            Array.isArray(
              action.payload?.data,
            )
              ? action.payload.data.length > 0
              : Boolean(
                  action.payload?.data,
                );
        },
      )

      .addCase(
        getOtherMedicalAvailability.rejected,
        (state, action) => {
          state.loading = false;

          state.error =
            action.payload ||
            "Failed to load availability.";
        },
      )

      // ===================================================
      // SAVE FULL WEEK
      // ===================================================

      .addCase(
        saveOtherMedicalAvailability.pending,
        (state) => {
          state.saving = true;

          state.error = null;

          state.successMessage = null;
        },
      )

      .addCase(
        saveOtherMedicalAvailability.fulfilled,
        (state, action) => {
          state.saving = false;

          state.error = null;

          state.successMessage =
            action.payload?.message ||
            "Week availability saved successfully.";

          if (
            action.payload?.data !==
            undefined
          ) {
            state.availability =
              action.payload.data;
          }

          state.exists = true;
        },
      )

      .addCase(
        saveOtherMedicalAvailability.rejected,
        (state, action) => {
          state.saving = false;

          state.error =
            action.payload ||
            "Failed to save week availability.";
        },
      )

      // ===================================================
      // UPDATE SINGLE DAY
      // ===================================================

      .addCase(
        updateSingleDay.pending,
        (state) => {
          state.actionLoading = true;

          state.error = null;
        },
      )

      .addCase(
        updateSingleDay.fulfilled,
        (state, action) => {
          state.actionLoading = false;

          state.error = null;

          state.successMessage =
            action.payload?.message ||
            "Day availability updated successfully.";

          if (
            action.payload?.data !==
            undefined
          ) {
            state.availability =
              action.payload.data;
          }
        },
      )

      .addCase(
        updateSingleDay.rejected,
        (state, action) => {
          state.actionLoading = false;

          state.error =
            action.payload ||
            "Failed to update day availability.";
        },
      )

      // ===================================================
      // UPDATE SINGLE SLOT
      // ===================================================

      .addCase(
        updateSingleSlot.pending,
        (state) => {
          state.actionLoading = true;

          state.error = null;
        },
      )

      .addCase(
        updateSingleSlot.fulfilled,
        (state, action) => {
          state.actionLoading = false;

          state.error = null;

          state.successMessage =
            action.payload?.message ||
            "Slot saved successfully.";

          if (
            action.payload?.data !==
            undefined
          ) {
            state.availability =
              action.payload.data;
          }
        },
      )

      .addCase(
        updateSingleSlot.rejected,
        (state, action) => {
          state.actionLoading = false;

          state.error =
            action.payload ||
            "Failed to save slot.";
        },
      )

      // ===================================================
      // CLEAR WEEK
      // ===================================================

      .addCase(
        clearWeek.pending,
        (state) => {
          state.actionLoading = true;

          state.error = null;

          state.successMessage = null;
        },
      )

      .addCase(
        clearWeek.fulfilled,
        (state, action) => {
          state.actionLoading = false;

          state.error = null;

          state.availability = [];

          state.exists = false;

          state.successMessage =
            action.payload?.message ||
            "Week availability cleared successfully.";
        },
      )

      .addCase(
        clearWeek.rejected,
        (state, action) => {
          state.actionLoading = false;

          state.error =
            action.payload ||
            "Failed to clear week.";
        },
      )

      // ===================================================
      // DELETE ALL
      // ===================================================

      .addCase(
        deleteAvailability.pending,
        (state) => {
          state.actionLoading = true;

          state.error = null;

          state.successMessage = null;
        },
      )

      .addCase(
        deleteAvailability.fulfilled,
        (state, action) => {
          state.actionLoading = false;

          state.error = null;

          state.availability = [];

          state.exists = false;

          state.successMessage =
            action.payload?.message ||
            "All availability deleted successfully.";
        },
      )

      .addCase(
        deleteAvailability.rejected,
        (state, action) => {
          state.actionLoading = false;

          state.error =
            action.payload ||
            "Failed to delete availability.";
        },
      );
  },
});

// =====================================================
// EXPORT ACTIONS
// =====================================================

export const {
  resetState,
  clearError,
  clearSuccessMessage,
} = otherMedicalAvailabilitySlice.actions;

// =====================================================
// EXPORT REDUCER
// =====================================================

export default otherMedicalAvailabilitySlice.reducer;
